import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getFormHeaderTranslations } from '@common/i18n/formHeader';
import { getLocalizedIntakeFormTitle } from '@common/i18n/matterTypes';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { Box, Link, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import LawFirmLogo from '@/components/LawFirmLogo';
import { LawyerInfo } from '@/pages/EmailResponsePages/ClientResponsePages/ClientResponseContent/ClientResponseContent';
import { convertToHtml } from '@/utils';
import FormContainer from '../FormContainer';
import { intakeFormContainerStyles } from '../styles';

export interface FormHeaderSectionProps {
    lawFirmId?: string;
    loading?: boolean;
    showLawyerInfo?: boolean;

    // Form state props - the component determines what to display based on these
    isSubmitted?: boolean;
    isDraftSubmitDisabled?: boolean;
    isDraftFormSubmitted?: boolean;
    formSubmissionId?: string | null;
    matterType?: string | null;
    inactiveTimerReached?: boolean;
    inactiveSaveFailed?: boolean;
    headerDescription?: string;
    footerTemplate?: string;

    // Status view props
    statusTitle?: string;
    statusMessage?: string;
    statusType?: string | null;

    intakeUrl?: string;
    language?: TemplateFormLanguage;
}

export default function FormHeaderSection({
    lawFirmId,
    loading = false,
    showLawyerInfo = false,
    isSubmitted = false,
    isDraftFormSubmitted = false,
    formSubmissionId,
    matterType,
    inactiveTimerReached = false,
    inactiveSaveFailed = false,
    headerDescription = '',
    footerTemplate = '',
    statusTitle,
    statusMessage,
    statusType,
    intakeUrl,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
}: FormHeaderSectionProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const t = getFormHeaderTranslations(language);

    if (loading) {
        return (
            <>
                <Skeleton
                    variant="rounded"
                    style={
                        isMobile
                            ? { width: '100px', height: '100px' }
                            : { width: '200px', height: '200px' }
                    }
                />
                <Stack spacing={1} sx={{ width: '100%', height: '100%' }}>
                    <Skeleton variant="text" sx={{ fontSize: '24px', width: '50%' }} />
                    <Skeleton variant="text" sx={{ fontSize: '14px', width: '40%' }} />
                </Stack>
            </>
        );
    }

    // Status view mode - when statusTitle is provided
    if (statusTitle !== undefined) {
        return (
            <Box sx={intakeFormContainerStyles}>
                <FormContainer>
                    <Box sx={{ display: 'flex', gap: '10px' }}>
                        <Box>
                            <LawFirmLogo lawFirmId={lawFirmId} />
                        </Box>
                        <Box>
                            <Typography variant={isMobile ? 'h3' : 'h1'}>{statusTitle}</Typography>
                            {statusMessage && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.primary',
                                        marginTop: isMobile ? '6px' : '16px',
                                        marginBottom: '16px',
                                        fontSize: isMobile ? '12px' : 'inherit',
                                        lineHeight: isMobile ? '16px' : 'inherit',
                                        wordWrap: 'break-word',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    {statusMessage}
                                </Typography>
                            )}
                            {(statusType === 'Forbidden' || statusType === 'IntakeCompleted') && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.primary',
                                        marginTop: '8px',
                                        fontSize: isMobile ? '12px' : '14px',
                                        lineHeight: isMobile ? '16px' : '20px',
                                    }}
                                >
                                    {t.descriptions.intakeCompletedFollowUp}
                                </Typography>
                            )}
                            {showLawyerInfo && <LawyerInfo />}
                        </Box>
                    </Box>
                </FormContainer>
            </Box>
        );
    }

    // Form view mode - compute title and description based on state
    const isPostIntakeTimeout = inactiveTimerReached && !!formSubmissionId;
    const title = isSubmitted
        ? inactiveTimerReached
            ? isPostIntakeTimeout || inactiveSaveFailed
                ? t.submitted.sessionEnded
                : t.submitted.submissionSaved
            : isDraftFormSubmitted
              ? t.submitted.draftSubmitted
              : t.submitted.thankYou
        : getLocalizedIntakeFormTitle(matterType, language);

    const descriptionStyles = {
        color: theme.palette.neutral.black,
        marginTop: isMobile ? '6px' : '16px',
        fontSize: isMobile ? '12px' : 'inherit',
        lineHeight: isMobile ? '16px' : 'inherit',
        wordWrap: 'break-word',
        overflowWrap: 'anywhere',
    };

    return (
        <Box sx={intakeFormContainerStyles}>
            <FormContainer>
                <LawFirmLogo lawFirmId={lawFirmId} />
                <Box>
                    <Typography variant={isMobile ? 'h3' : 'h1'}>{title}</Typography>
                    {inactiveTimerReached ? (
                        <Typography variant="body2" sx={descriptionStyles}>
                            {isPostIntakeTimeout || inactiveSaveFailed
                                ? t.descriptions.sessionEndedRefresh
                                : t.descriptions.submissionSavedRefresh}
                        </Typography>
                    ) : isDraftFormSubmitted ? (
                        <Typography variant="body2" sx={descriptionStyles}>
                            {t.descriptions.draftSubmitted}
                        </Typography>
                    ) : (
                        <Typography
                            variant="body2"
                            sx={descriptionStyles}
                            dangerouslySetInnerHTML={{
                                __html: convertToHtml(
                                    isSubmitted ? footerTemplate : headerDescription,
                                ),
                            }}
                        />
                    )}
                    {(isDraftFormSubmitted || inactiveTimerReached) && intakeUrl && (
                        <Link
                            href={intakeUrl}
                            variant="body2"
                            sx={{
                                marginTop: '8px',
                                display: 'block',
                            }}
                        >
                            {t.refreshLink}
                        </Link>
                    )}
                    {showLawyerInfo && <LawyerInfo />}
                </Box>
            </FormContainer>
        </Box>
    );
}
